import { useTranslation } from 'react-i18next'
import { HolidayExtent } from '@/api/model'
import { Label } from '@/components/shadcn/label'
import { RadioGroup, RadioGroupItem } from '@/components/shadcn/radio-group'

/**
 * Holiday field keys = exactly the DTO field names (spread directly into the request).
 * Labels come from the `service-modules` NS: `t(\`holidays.fields.\${key}\`)`.
 */
export const HOLIDAY_FIELDS = [
  { key: 'holy3KingsDayHoliday' },
  { key: 'internationalWomensHoliday' },
  { key: 'fronleichnamHoliday' },
  { key: 'mariaHimmelfahrtHoliday' },
  { key: 'worldChildrenDayHoliday' },
  { key: 'reformationDayHoliday' },
  { key: 'allerheiligenDayHoliday' },
  { key: 'bussUndBetTagHoliday' },
  { key: 'augsburgerFriedensfestHoliday' },
  { key: 'christmasEveHoliday' },
  { key: 'newYearsEveHoliday' },
  { key: 'easterSundayHoliday' },
  { key: 'pfingstSundayHoliday' },
] as const

export type RegionalHolidayKey = (typeof HOLIDAY_FIELDS)[number]['key']
export type RegionalHolidaysState = Record<RegionalHolidayKey, HolidayExtent>

/** All regional holidays set to "no holiday" (default for new quotas). */
export const defaultRegionalHolidays: RegionalHolidaysState = Object.fromEntries(
  HOLIDAY_FIELDS.map((h) => [h.key, HolidayExtent.None]),
) as RegionalHolidaysState

interface Props {
  value: RegionalHolidaysState
  onChange: (value: RegionalHolidaysState) => void
}

/** Extent options per holiday (none/half/full) as a radio group. */
const HOLIDAY_EXTENTS: HolidayExtent[] = [HolidayExtent.None, HolidayExtent.Half, HolidayExtent.Full]

/**
 * Regional holidays: vertical list, per holiday the name on the left and on the
 * right a horizontal radio group for the extent (none/half/full) — like the old portal.
 */
export function RegionalHolidays({ value, onChange }: Props) {
  const { t } = useTranslation('service-modules-absence')
  return (
    <div className="flex flex-col gap-3">
      <span className="text-sm font-medium">{t('holidays.title')}</span>
      <div className="flex flex-col divide-y">
        {HOLIDAY_FIELDS.map((holiday) => (
          <div key={holiday.key} className="flex items-center justify-between gap-4 py-2">
            <span className="text-sm">{t(`holidays.fields.${holiday.key}`)}</span>
            <RadioGroup
              value={value[holiday.key]}
              onValueChange={(v) => onChange({ ...value, [holiday.key]: v as HolidayExtent })}
              className="flex w-auto shrink-0 flex-row gap-4"
            >
              {HOLIDAY_EXTENTS.map((extent) => {
                const id = `${holiday.key}-${extent}`
                return (
                  <div key={extent} className="flex items-center gap-1.5">
                    <RadioGroupItem id={id} value={extent} />
                    <Label htmlFor={id} className="text-xs font-normal text-muted-foreground">
                      {t(`holidays.extent.${extent}`)}
                    </Label>
                  </div>
                )
              })}
            </RadioGroup>
          </div>
        ))}
      </div>
    </div>
  )
}
